import { PsHttpService, type PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import type { ReverseEngineeringApi } from '../re-api';
import type {
  ChatInput,
  ChatResponse,
} from '@/components/pages/reverse-engineering/types/message.types';
import type {
  SearchResultRequest,
  SearchResultResponse,
} from '@/components/pages/reverse-engineering/types/search.types';

@injectable()
export class ReverseEngineeringApiImpl extends PsHttpService implements ReverseEngineeringApi {
  @inject(FactoryPsHttpSSE)
  private _factorySsePsHttp!: FactoryPsHttpSSE;

  private isLocal = import.meta.env.DEV;
  private domain = this.isLocal ? 'http://localhost:8000' : import.meta.env.VITE_EUREKA_SERVICE_URI;
  private headers = {
    'X-API-Version': '1.0',
    ...(this.isLocal && { 'X-User-Id': 'Local-User-Dev' }),
  };

  get options() {
    return {
      baseURL: `${this.domain}/api/mse-ci`,
      headers: this.headers,
    };
  }

  chat(input: ChatInput): Promise<ChatResponse> {
    return this.post<PatSnapCommonResponseData<ChatResponse>>({
      baseURL: this.options.baseURL,
      url: '/agent/chat',
      data: input,
    }).then((res) => {
      return res.data as ChatResponse;
    });
  }

  connect(conversation_id: string) {
    return this._factorySsePsHttp<Record<any, any>>()
      .init({
        method: 'GET',
        url: `${this.options.baseURL}/agent/connect/${conversation_id}`,
        hasFinalMessage: () => true,
      })
      .setFetchHeaders(this.headers);
  }

  getSearchResult(input: SearchResultRequest): Promise<SearchResultResponse> {
    return this.post<PatSnapCommonResponseData<SearchResultResponse>>({
      baseURL: this.options.baseURL,
      url: '/agent/re/search-result',
      data: input,
    }).then((res) => {
      return res.data as SearchResultResponse;
    });
  }

  getProcesses(input: Record<any, any>): Promise<Record<any, any>> {
    return this.post<PatSnapCommonResponseData<SearchResultResponse>>({
      baseURL: this.options.baseURL,
      url: '/agent/re/process-comparison',
      data: input,
    }).then((res) => {
      return res.data;
    });
  }

  shareConversation(cid: string): Promise<Record<any, any>> {
    return this.post<PatSnapCommonResponseData<SearchResultResponse>>({
      baseURL: this.options.baseURL,
      url: `/agent/share/${cid}`,
    }).then((res) => {
      return res.data;
    });
  }

  connectShare(conversation_id: string) {
    return this._factorySsePsHttp<Record<any, any>>()
      .init({
        method: 'GET',
        url: `${this.options.baseURL}/share/agent/connect/${conversation_id}`,
        hasFinalMessage: () => true,
      })
      .setFetchHeaders(this.headers);
  }

  exportReport(
    conversation_id: string,
    content_id: string,
    step_content_id: string,
    export_file_type: 'pdf' | 'docx',
  ) {
    return this.post<PatSnapCommonResponseData<Record<any, any>>>({
      baseURL: this.options.baseURL,
      url: '/agent/re/export-report',
      data: {
        conversation_id,
        content_id,
        step_content_id,
        export_file_type,
      },
    }).then((res: any) => {
      return res.data as Record<any, any>
    });
  }

  async downloadReport(
    conversation_id: string,
    content_id: string,
    step_content_id: string,
    export_file_type: 'pdf' | 'docx',
    filename: string,
  ): Promise<Blob> {
    const blob = (await this.get({
      baseURL: this.options.baseURL,
      url: '/agent/re/export-report/download',
      params: {
        conversation_id,
        content_id,
        step_content_id,
        export_file_type,
        filename,
      },
      responseType: 'blob',
    })) as unknown as Blob;

    if (!blob?.size || String(blob.type || '').includes('application/json')) {
      throw new Error('export report download failed');
    }

    return blob;
  }
}
